// Domain → Badge status mapping. Keeps "paid"/"overdue"/etc. learnable across screens.
// Copper is never a status (see colors.ts). Maps each domain status string to a
// { key, label } the Badge can render.
import type { StatusKey } from '../../theme';

type BadgeSpec = { key: StatusKey; label: string };

/** Invoice status → badge. */
export function invoiceBadge(status: string): BadgeSpec {
  switch (status) {
    case 'paid':
      return { key: 'paid', label: 'Paid' };
    case 'partial':
      return { key: 'inProgress', label: 'Partial' };
    case 'overdue':
      return { key: 'overdue', label: 'Overdue' };
    case 'sent':
      return { key: 'sent', label: 'Sent' };
    case 'void':
      return { key: 'draft', label: 'Void' };
    default:
      return { key: 'draft', label: 'Draft' };
  }
}

/** Estimate approval/status → badge. Approved is rendered in the paid/done style. */
export function estimateBadge(status: string): BadgeSpec {
  switch (status) {
    case 'approved':
      return { key: 'paid', label: 'Approved' };
    case 'signed':
      return { key: 'paid', label: 'Signed' };
    case 'presented':
    case 'sent':
      return { key: 'sent', label: 'Presented' };
    case 'rejected':
      return { key: 'overdue', label: 'Rejected' };
    default:
      return { key: 'draft', label: 'Draft' };
  }
}

/** Job phase status → badge. */
export function phaseBadge(status: string): BadgeSpec {
  switch (status) {
    case 'done':
    case 'complete':
      return { key: 'paid', label: 'Done' };
    case 'in_progress':
      return { key: 'inProgress', label: 'In progress' };
    case 'blocked':
      return { key: 'overdue', label: 'Blocked' };
    default:
      return { key: 'draft', label: 'Not started' };
  }
}

/** Priority → badge. urgent/high read as attention; medium as info; low as draft. */
export function priorityBadge(priority: string): BadgeSpec {
  switch (priority) {
    case 'urgent':
      return { key: 'overdue', label: 'Urgent' };
    case 'high':
      return { key: 'inProgress', label: 'High' };
    case 'medium':
      return { key: 'sent', label: 'Medium' };
    default:
      return { key: 'draft', label: 'Low' };
  }
}

/** Task status → badge. */
export function taskStatusBadge(status: string): BadgeSpec {
  switch (status) {
    case 'done':
      return { key: 'paid', label: 'Done' };
    case 'in_progress':
      return { key: 'inProgress', label: 'In progress' };
    case 'cancelled':
      return { key: 'draft', label: 'Cancelled' };
    default:
      return { key: 'sent', label: 'Open' };
  }
}

/** Work-queue entry kind → badge (Task / Punchlist / Event). */
export function queueKindBadge(kind: string): BadgeSpec {
  switch (kind) {
    case 'task':
      return { key: 'sent', label: 'Task' };
    case 'punchlist_item':
      return { key: 'inProgress', label: 'Punchlist' };
    case 'schedule_event':
      return { key: 'paid', label: 'Event' };
    default:
      return { key: 'draft', label: kind };
  }
}

/** License status → badge (account screen). */
export function licenseBadge(status: string): BadgeSpec {
  switch (status) {
    case 'active':
    case 'trialing':
      return { key: 'paid', label: status === 'trialing' ? 'Trialing' : 'Active' };
    case 'past_due':
      return { key: 'overdue', label: 'Past due' };
    case 'canceled':
    case 'unpaid':
      return { key: 'overdue', label: status === 'unpaid' ? 'Unpaid' : 'Canceled' };
    default:
      return { key: 'draft', label: status };
  }
}
